import React, { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import ModalComponent from '..'

import {
  areArraysSame,
  calculateVariantCombinationPriceRaw,
  formatCurrency,
  sumArray,
  transformVariants,
} from '../../../helpers'

import { useAppSelector } from '../../../hooks'

import {
  ComboSelection,
  ComboSelections,
} from '../../../redux-toolkits/products/products.type'

import {
  Combination,
  ComboItemParent,
  Product,
  TransformedVariant,
  VariantCombination,
} from '../../../redux-toolkits/products/products.type'
import { Button } from '../../button'
import Icon from '../../icon'
import { TabNav } from '../../nav/tabNav'
import { HeaderSubheader } from '../../typography/HeaderSubheader'

type ComboSelectionGroup = Omit<ComboItemParent, 'items'> & {
  items: ComboSelection[]
}

type ComboGroupValidationMethod = 'uniqueItemsSelected' | 'totalItemUnits'

export const flattenComboSelections = (
  comboGroups: ComboSelectionGroup[],
): ComboSelections => {
  return comboGroups?.flatMap((group) => group.items ?? []) ?? []
}

export const ChooseProductOptionsModal: React.FC<{
  open: boolean
  setOpen: (val: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (
    product: Product,
    comboSelections: ComboSelectionGroup[],
    variantCombination?: VariantCombination,
  ) => void
  product: Product
  loading: boolean
  comboGroupValidation?: ComboGroupValidationMethod
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  product,
  loading,
  comboGroupValidation = 'totalItemUnits',
}) => {
  const [comboSelections, setComboSelections] = useState<ComboSelectionGroup[]>(
    [],
  )
  const [selectedVariants, setSelectedVariants] = useState<Combination[]>([])
  const [variantComboSelection, setVariantComboSelection] =
    useState<VariantCombination>()
  const [transformedVariants, setTransformedVariants] = useState<
    TransformedVariant[]
  >([])

  const { adminCartItems } = useAppSelector((state) => state.auth)

  // INITIAL COMBO SELECTION SETUP
  useEffect(() => {
    const adminCartComboItems = adminCartItems
      ?.map((item) => item.comboItems)
      ?.flat()

    setComboSelections(
      product?.comboItems?.map((comboGroup) => ({
        ...comboGroup,
        items:
          comboGroup?.items?.map((item) => ({
            combo: item,
            quantity: 0,
            // adminCartComboItems?.find((cartItem) => cartItem._id === item._id)
            //   ?.quantity ?? 0,
          })) ?? [],
      })),
    )
  }, [adminCartItems])

  // TRANSFORM PRODUCT VARIANTS
  useEffect(() => {
    setTransformedVariants(transformVariants(product.variants))
  }, [product.variants])

  // FIND SELECTED VARIANT COMBO
  useEffect(() => {
    const selectedCombination = product.variantCombinations?.find(
      (combination) => {
        const variantIdsMatch = areArraysSame(
          combination.variantCombination.map((v) => v.variant._id),
          selectedVariants.map((v) => v.variant._id),
        )

        const valuesMatch = areArraysSame(
          combination.variantCombination.map((v) => v.value),
          selectedVariants.map((v) => v.value),
        )

        return variantIdsMatch && valuesMatch
      },
    )

    setVariantComboSelection(selectedCombination)
  }, [selectedVariants, product.variantCombinations])

  // TOTAL PRICE (useMemo)
  const totalPrice = useMemo(() => {
    const basePrice = product.price

    const markup = ((product.markupValue ?? 0) / 100) * basePrice

    const comboPrice = sumArray(
      flattenComboSelections(comboSelections).map(
        (item) => item.combo.price * item.quantity,
      ),
    )

    const variantPrice = calculateVariantCombinationPriceRaw(
      variantComboSelection?.variantCombination ?? [],
      product.variants,
    )

    return basePrice + markup + comboPrice + variantPrice
  }, [product, comboSelections, variantComboSelection])

  // COMBO QUANTITY UPDATE HELPER
  const updateComboQuantity = (
    parentId: string,
    comboId: string,
    delta: number,
  ) => {
    setComboSelections((previousSelections) =>
      previousSelections.map((parent) =>
        parent._id === parentId
          ? {
              ...parent,
              items: parent.items.map((item) =>
                item.combo._id === comboId
                  ? {
                      combo: item.combo,
                      quantity: item.quantity + delta,
                    }
                  : item,
              ),
            }
          : parent,
      ),
    )
  }

  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={onCloseCallback}
      title={
        <div className="flex flex-col gap-2">
          {product.name}
          <p className="text-sec-black text-base font-ttForsTrial font-normal">
            {formatCurrency(totalPrice)}
          </p>
        </div>
      }
      childrenClassName="md:!max-w-[420px]"
    >
      <div className="flex flex-col gap-6">
        {/* PRODUCT IMAGE */}
        <img
          alt={product.name}
          src={product.images[0]}
          className="w-full max-h-[210px] shadow-md aspect-square object-cover rounded-md overflow-hidden"
        />

        {/* COMBO OPTIONS */}
        {product.isCombo && (product?.comboItems?.length ?? 0) > 0
          ? comboSelections.map((comboGroup) => {
              return (
                <div key={comboGroup.groupName}>
                  <HeaderSubheader
                    title={comboGroup.groupName}
                    text={comboGroup.groupDescription}
                    className="mb-4"
                  />

                  <div>
                    {comboGroup.items.map((comboItem) => {
                      const selectedItems = comboGroup.items.filter(
                        (i) => i.quantity > 0,
                      )
                      const totalGroupQuantity = comboGroup.items.reduce(
                        (sum, item) => sum + item.quantity,
                        0,
                      )

                      const selectedIds = selectedItems.map((i) => i.combo._id)

                      const isItemAlreadySelected = selectedIds.includes(
                        comboItem.combo._id,
                      )

                      // counts total units across all items e.g. selecting 3 of the same item counts as 3 toward the group max.
                      // OR
                      // treats each distinct item as 1 unit e.g. selecting 3 of the same item only counts as 1 toward the group max.
                      const isGroupQuantityLimitReached =
                        comboGroupValidation === 'totalItemUnits'
                          ? totalGroupQuantity >= comboGroup.maximumQuantity
                          : selectedItems.length >= comboGroup.maximumQuantity

                      return (
                        <div
                          key={comboItem.combo._id}
                          className="flex items-center justify-between gap-4 flex-wrap border-b last:border-b-0 py-4 first:pt-0 last:pb-0"
                        >
                          <div className="flex flex-col gap-2">
                            <p>{comboItem.combo.name}</p>
                            <p className="text-sec-black">
                              {`+ ${formatCurrency(comboItem.combo.price)}`}
                            </p>
                          </div>

                          <div className="flex items-center gap-4">
                            {/* MINUS BUTTON */}
                            <button
                              type="button"
                              onClick={() =>
                                updateComboQuantity(
                                  comboGroup._id,
                                  comboItem.combo._id,
                                  -1,
                                )
                              }
                              className="disabled:opacity-20 disabled:cursor-not-allowed w-fit"
                              disabled={comboItem.quantity <= 0}
                            >
                              <Icon id="minus-circle" width={24} height={24} />
                            </button>

                            <p>{comboItem.quantity}</p>

                            {/* PLUS BUTTON */}
                            <button
                              type="button"
                              onClick={() =>
                                updateComboQuantity(
                                  comboGroup._id,
                                  comboItem.combo._id,
                                  1,
                                )
                              }
                              className="disabled:opacity-20 disabled:cursor-not-allowed w-fit"
                              disabled={
                                comboItem.quantity >=
                                  comboItem.combo.maximumQuantity ||
                                (!isItemAlreadySelected &&
                                  isGroupQuantityLimitReached)
                              }
                            >
                              <Icon id="plus-circle" width={24} height={24} />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )
            })
          : undefined}

        {/* VARIANTS */}
        {product.isVariant ? (
          <div className="flex flex-col gap-6">
            {transformedVariants.map((transformedVariant, variantIndex) => {
              const validCombinations = product.variantCombinations.filter(
                (variantCombination) =>
                  selectedVariants
                    .filter(
                      (v) => v.variant._id !== transformedVariant.variant._id,
                    )
                    .every((selectedVariant) => {
                      const variantIds =
                        variantCombination.variantCombination.map(
                          (i) => i.variant._id,
                        )

                      const values = variantCombination.variantCombination.map(
                        (i) => i.value,
                      )

                      return (
                        variantIds.includes(selectedVariant.variant._id) &&
                        values.includes(selectedVariant.value)
                      )
                    }),
              )

              return (
                <div
                  key={`${variantIndex}-${transformedVariant.variant?.name}`}
                  className="flex flex-col gap-4"
                >
                  <HeaderSubheader
                    title={`Select ${transformedVariant.variant?.name}`}
                  />

                  <TabNav
                    activeTab={
                      selectedVariants.find(
                        (v) => v.variant._id === transformedVariant.variant._id,
                      )?.value ?? ''
                    }
                    setActiveTab={() => {}}
                    tabs={transformedVariant.values.map((value) => {
                      const isAvailable = validCombinations.some(
                        (combination) =>
                          combination.inventoryCount > 0 &&
                          combination.isActive &&
                          combination.variantCombination
                            .map((i) => i.value)
                            .includes(value),
                      )

                      return {
                        id: value,
                        name: value,
                        disabled: !isAvailable,
                        onClick: () => {
                          const alreadySelected = selectedVariants
                            .map((v) => v.variant._id)
                            .includes(transformedVariant.variant._id)

                          const selectedValue = selectedVariants.find(
                            (v) =>
                              v.variant._id === transformedVariant.variant._id,
                          )?.value

                          if (alreadySelected && selectedValue === value) {
                            setSelectedVariants((prev) =>
                              prev.filter(
                                (v) =>
                                  v.variant._id !==
                                  transformedVariant.variant._id,
                              ),
                            )
                          } else {
                            setSelectedVariants((prev) => [
                              ...prev.filter(
                                (v) =>
                                  v.variant._id !==
                                  transformedVariant.variant._id,
                              ),
                              {
                                variant: transformedVariant.variant,
                                value,
                              },
                            ])
                          }
                        },
                      }
                    })}
                    type="switcher-outline"
                    className="gap-2"
                    tabClassName="border disabled:cursor-not-allowed disabled:line-through disabled:opacity-50"
                  />
                </div>
              )
            })}
          </div>
        ) : undefined}

        {/* ADD TO CART */}
        <Button
          type="button"
          primary
          label="Add to cart"
          className="w-full mt-4 sticky bottom-0 z-[100]"
          loading={loading}
          disabled={loading || (product.isVariant && !variantComboSelection)}
          onClick={() => {
            if (
              product.isVariant &&
              (variantComboSelection?.inventoryCount ?? 0) <= 0
            ) {
              toast.error('Selected variant combination is out of stock')
              return
            }

            if (product.isCombo) {
              let errorExists = false

              comboSelections.forEach((group) => {
                const selectedItemsCount = group.items.filter(
                  (i) => i.quantity > 0,
                ).length

                const totalGroupQuantity = group.items.reduce(
                  (sum, item) => sum + item.quantity,
                  0,
                )

                if (
                  (selectedItemsCount < group.minimumQuantity &&
                    comboGroupValidation === 'uniqueItemsSelected') ||
                  (totalGroupQuantity < group.minimumQuantity &&
                    comboGroupValidation === 'totalItemUnits')
                ) {
                  toast.error(
                    `Choose at least ${group.minimumQuantity} options from '${group.groupName}'`,
                  )

                  errorExists = true
                }
              })

              if (errorExists) return
            }

            onSuccessCallback(product, comboSelections, variantComboSelection)
          }}
        />
      </div>
    </ModalComponent>
  )
}
